/**
 * Institutional Certificates admin page.
 *
 * Super-admin / admin can pick one of six preset certificate templates
 * (BMW Annual Compliance, Sanitation Audit, LSGD Ward Clearance,
 * Doorstep Contract, Staff Safety Training, CSR Contribution), fill in
 * the template placeholders and generate a PDF that carries a QR
 * pointing to the public verify page.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Certificate, DownloadSimple, Plus, X, Trash, ShieldCheck,
} from "@phosphor-icons/react";

const TYPE_ACCENTS = {
  bmw_annual_compliance: "#2D5A27",
  sanitation_audit: "#0B2B66",
  lsgd_ward_clearance: "#7A2E18",
  doorstep_contract: "#5C5A55",
  staff_safety_training: "#1B5E20",
  csr_contribution: "#0D47A1",
};

export default function InstitutionalCerts() {
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [openType, setOpenType] = useState(null);         // template selected for creation
  const [form, setForm] = useState({ institution_name: "", contact_email: "", fields: {} });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, l] = await Promise.all([
        api.get("/institutional-cert-types"),
        api.get("/institutional-certs"),
      ]);
      setTypes(t.data);
      setItems(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = (t) => {
    setOpenType(t);
    const emptyFields = {};
    for (const p of t.placeholders) emptyFields[p] = "";
    setForm({ institution_name: "", contact_email: "", fields: emptyFields });
  };
  const close = () => setOpenType(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.institution_name.trim()) {
      toast.error("Institution name is required"); return;
    }
    setBusy(true);
    try {
      const res = await api.post("/institutional-certs", {
        cert_type: openType.type_id,
        institution_name: form.institution_name.trim(),
        contact_email: form.contact_email.trim() || null,
        fields: form.fields,
      });
      toast.success(`Certificate ${res.data.cert_no} generated`);
      close();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const download = async (c) => {
    try {
      const res = await api.get(`/institutional-certs/${c.id}/pdf`,
                                 { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${c.cert_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete certificate ${c.cert_no}?`)) return;
    try {
      await api.delete(`/institutional-certs/${c.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const typeMap = useMemo(() => {
    const m = {};
    for (const t of types) m[t.type_id] = t;
    return m;
  }, [types]);

  return (
    <AppShell title="Institutional Certificates"
              subtitle="Generate 6 preset PDF certificates with QR verification">
      <section className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">
          Choose a template
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inst-cert-templates">
          {types.map(t => (
            <button key={t.type_id} onClick={() => openNew(t)}
                    data-testid={`tpl-${t.type_id}`}
                    className="text-left bg-white border border-[#E5E2D9] hover:border-[#2D5A27] hover:shadow rounded-md p-5 transition-all group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 rounded-md flex items-center justify-center text-white"
                     style={{ background: TYPE_ACCENTS[t.type_id] || "#2D5A27" }}>
                  <Certificate size={18} weight="duotone"/>
                </div>
                <div className="text-[10px] font-mono text-[#8A8780]">{t.prefix}</div>
              </div>
              <div className="font-heading text-base text-[#1A1A1A] leading-snug">{t.title}</div>
              <div className="text-xs text-[#5C5A55] mt-1">{t.subtitle}</div>
              <div className="text-[10px] text-[#8A8780] mt-3 uppercase tracking-widest">
                For · {t.audience}
              </div>
              <div className="mt-3 text-xs text-[#2D5A27] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                <Plus size={12} weight="bold"/> Issue certificate
              </div>
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">
          Issued certificates
        </div>
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="inst-cert-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Cert No.</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Institution</th>
                <th className="px-6 py-3">Issued</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-[#8A8780]">
                  No certificates issued yet. Choose a template above to create your first one.
                </td></tr>
              )}
              {items.map((c, i) => {
                const t = typeMap[c.cert_type];
                return (
                  <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`inst-cert-row-${c.id}`}>
                    <td className="px-6 py-3 font-mono text-xs text-[#2D5A27]">{c.cert_no}</td>
                    <td className="px-6 py-3">
                      <span className="text-xs" style={{ color: TYPE_ACCENTS[c.cert_type] }}>
                        {t?.title || c.cert_type}
                      </span>
                    </td>
                    <td className="px-6 py-3 font-medium">{c.institution_name}</td>
                    <td className="px-6 py-3 text-xs text-[#5C5A55]">
                      {c.issued_at ? c.issued_at.substring(0, 10) : "—"}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button data-testid={`inst-cert-dl-${c.id}`} onClick={() => download(c)}
                              className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-3">
                        <DownloadSimple size={12}/>PDF
                      </button>
                      <button data-testid={`inst-cert-del-${c.id}`} onClick={() => remove(c)}
                              className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                        <Trash size={12}/>Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {openType && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-md w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
               data-testid="inst-cert-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  New certificate · {openType.prefix}
                </div>
                <div className="font-heading text-lg mt-1">{openType.title}</div>
              </div>
              <button onClick={close} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <Field label="Institution / Recipient name" required
                     testid="inst-name"
                     value={form.institution_name}
                     onChange={v => setForm(f => ({ ...f, institution_name: v }))}/>
              <Field label="Contact email (optional)"
                     testid="inst-email" type="email"
                     value={form.contact_email}
                     onChange={v => setForm(f => ({ ...f, contact_email: v }))}/>

              {openType.placeholders.length > 0 && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                    Template variables
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {openType.placeholders.map(p => (
                      <Field key={p} label={p.replace(/_/g, " ")}
                             testid={`inst-var-${p}`}
                             value={form.fields[p] || ""}
                             onChange={v => setForm(f => ({ ...f, fields: { ...f.fields, [p]: v } }))}/>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-[#E5E2D9]">
                <div className="text-[10px] text-[#8A8780] inline-flex items-center gap-1">
                  <ShieldCheck size={12} color="#2D5A27"/>
                  QR-verifiable at /verify/institutional-cert/&lt;no&gt;
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={close} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="inst-cert-submit"
                          className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium disabled:opacity-50">
                    {busy ? "Generating…" : "Generate certificate"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1 capitalize">{label}{required && " *"}</span>
      <input type={type} value={value}
             onChange={e => onChange(e.target.value)} required={required}
             data-testid={testid}
             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm focus:border-[#2D5A27] outline-none"/>
    </label>
  );
}
